import React from "react";
import {
  FaChartLine,
  FaMedal,
  FaPercentage,
  FaRedo,
  FaTrophy,
} from "react-icons/fa";
import { GiPodium } from "react-icons/gi";

import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";
import ScrollIndicator from "../../Home/ScrollIndicator";
import useScrollToTop from "../../../hooks/useScollToTop";

import RecentResultsTimeline from "./components/RecentResultsTimeline";
import StatistiqueCard from "./components/StatistiqueCard";
import StatistiquesChartsGrid from "./components/StatistiquesChartsGrid";
import StatistiquesEmptyState from "./components/StatistiquesEmptyState";
import StatistiquesHero from "./components/StatistiquesHero";
import StatistiquesSkeleton from "./components/StatistiquesSkeleton";
import StatistiquesToolbar from "./components/StatistiquesToolbar";

import { useStatistiquesJudokaPage } from "./hooks/useStatistiquesJudokaPage";

function StatistiquesJudokaPage() {
  useScrollToTop();

  const {
    events,
    filteredEvents,
    filters,
    loading,
    refreshing,
    stats,
    chartData,
    seasons,
    activeFiltersCount,
    fetchData,
    handleFilterChange,
    resetFilters,
  } = useStatistiquesJudokaPage();

  return (
    <main className="min-h-screen bg-slate-50 pb-24 pt-[88px] text-slate-950 dark:bg-slate-950 dark:text-white lg:pt-[104px]">
      <StatistiquesHero stats={stats} />

      <section className="mx-auto -mt-12 w-full max-w-[1280px] px-4 sm:px-6 lg:px-8">
        <div className="rounded-[2rem] border border-white/80 bg-white/95 p-4 shadow-2xl shadow-slate-200/70 backdrop-blur-xl dark:border-white/10 dark:bg-slate-900/95 dark:shadow-black/30 sm:p-6">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-sky-100 px-3 py-2 text-xs font-bold uppercase tracking-[0.16em] text-sky-700 dark:bg-sky-500/10 dark:text-sky-200">
                <FaChartLine />
                Analyse sportive
              </div>

              <h1 className="mt-4 font-titre text-3xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-4xl">
                Statistiques de combats
              </h1>

              <p className="mt-3 max-w-3xl text-sm font-medium leading-7 text-slate-600 dark:text-white/60 sm:text-base">
                Analyse tes compétitions, ton taux de victoire, tes podiums, tes
                séries de combats et ta progression saison après saison.
              </p>
            </div>

            <button
              type="button"
              onClick={fetchData}
              disabled={refreshing}
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:-translate-y-0.5 hover:border-sky-200 hover:text-sky-700 disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/10 dark:bg-white/[0.05] dark:text-white dark:hover:border-sky-300/40 dark:hover:text-sky-100 lg:min-w-[220px]"
            >
              <FaRedo className={refreshing ? "animate-spin" : ""} />
              {refreshing ? "Actualisation..." : "Actualiser"}
            </button>
          </div>

          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <StatistiqueCard
              icon={FaTrophy}
              label="Compétitions effectuées"
              value={stats.completedCompetitions}
              description="Compétitions passées dans ton calendrier."
              tone="sky"
            />

            <StatistiqueCard
              icon={FaPercentage}
              label="Taux de victoire"
              value={`${stats.winRate}%`}
              description="Part des combats gagnés."
              tone="emerald"
            />

            <StatistiqueCard
              icon={GiPodium}
              label="Podiums"
              value={stats.podiums}
              description="Classements 1er, 2ème ou 3ème."
              tone="amber"
            />

            <StatistiqueCard
              icon={FaMedal}
              label="Meilleure série"
              value={stats.bestWinStreak}
              description="Plus longue série de victoires."
              tone="orange"
            />
          </div>
        </div>
      </section>

      <section className="mx-auto mt-6 w-full max-w-[1280px] px-4 sm:px-6 lg:px-8">
        <StatistiquesToolbar
          filters={filters}
          seasons={seasons}
          totalCount={events.length}
          resultsCount={filteredEvents.length}
          activeFiltersCount={activeFiltersCount}
          onFilterChange={handleFilterChange}
          onReset={resetFilters}
        />

        {loading ? (
          <StatistiquesSkeleton />
        ) : events.length === 0 ? (
          <StatistiquesEmptyState />
        ) : filteredEvents.length === 0 ? (
          <StatistiquesEmptyState
            title="Aucune donnée pour ce filtre"
            description="Aucune compétition ne correspond à la saison ou aux filtres sélectionnés."
            onReset={resetFilters}
          />
        ) : (
          <>
            <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <StatistiqueCard
                icon={FaTrophy}
                label="Compétitions à venir"
                value={stats.upcomingCompetitions}
                description="Événements programmés."
                tone="slate"
              />

              <StatistiqueCard
                icon={FaChartLine}
                label="Total combats"
                value={stats.totalFights}
                description={`✅ ${stats.wins} · ❌ ${stats.losses} · N ${stats.draws}`}
                tone="blue"
              />

              <StatistiqueCard
                icon={FaPercentage}
                label="Taux de podium"
                value={`${stats.podiumRate}%`}
                description="Podiums sur compétitions effectuées."
                tone="amber"
              />

              <StatistiqueCard
                icon={FaMedal}
                label="Moyenne combats"
                value={stats.averageFightsPerCompetition}
                description="Combats par compétition avec résultat."
                tone="emerald"
              />
            </div>

            <StatistiquesChartsGrid chartData={chartData} stats={stats} />

            <RecentResultsTimeline events={chartData.recentResults} />
          </>
        )}
      </section>

      <DashboardNavigation />
      <ScrollIndicator />
    </main>
  );
}

export default StatistiquesJudokaPage;
